import { Component, ReactNode, ErrorInfo } from "react";
import { logError } from "@/lib/errorLogger";
import { Sentry } from "@/lib/sentry";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  sectionName?: string;
  level?: "page" | "widget";
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`[ErrorBoundary:${this.props.sectionName || "Unknown"}]`, error, errorInfo);
    logError(error, this.props.sectionName || "Unknown", null, "Component render crashed");
    Sentry.withScope((scope) => {
      scope.setTag("section", this.props.sectionName || "Unknown");
      scope.setTag("boundary_level", this.props.level || "page");
      scope.setContext("react", { componentStack: errorInfo.componentStack });
      Sentry.captureException(error);
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      if (this.props.level === "widget") {
        return <WidgetErrorFallback onRetry={this.handleRetry} sectionName={this.props.sectionName} />;
      }

      return (
        <PageErrorFallback
          onRetry={this.handleRetry}
          error={this.state.error}
          sectionName={this.props.sectionName}
        />
      );
    }

    return this.props.children;
  }
}

function PageErrorFallback({ onRetry, error, sectionName }: { onRetry: () => void; error: Error | null; sectionName?: string }) {
  return (
    <div className="flex items-center justify-center min-h-[60vh] p-6">
      <div className="bg-card rounded-2xl shadow-lg border p-8 max-w-md w-full text-center space-y-4">
        {/* Nursly Logo */}
        <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center mx-auto">
          <span className="text-primary-foreground font-display font-extrabold text-lg">C</span>
        </div>

        {/* Warning icon */}
        <div className="text-5xl">⚠️</div>

        <h2 className="text-xl font-display font-bold text-foreground">
          Oops! Something went a bit wrong.
        </h2>

        <p className="text-sm text-muted-foreground leading-relaxed">
          This page ran into a problem and couldn't load. Nothing you saved has been lost.
          Refresh the page to try again, or go back to the dashboard. If it keeps happening,
          email <a href="mailto:support@nursly.co.uk" className="underline">support@nursly.co.uk</a>.
        </p>

        <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center rounded-xl bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            Refresh This Page
          </button>
          <button
            onClick={onRetry}
            className="inline-flex items-center justify-center rounded-xl border border-border bg-card text-foreground px-5 py-2.5 text-sm font-semibold hover:bg-muted transition-colors"
          >
            Try Again
          </button>
          <a
            href="/dashboard"
            className="inline-flex items-center justify-center rounded-xl border border-border bg-card text-foreground px-5 py-2.5 text-sm font-semibold hover:bg-muted transition-colors"
          >
            Go Back to Dashboard
          </a>
        </div>

        {/* Technical details toggle */}
        {error && <TechnicalDetails error={error} sectionName={sectionName} />}
      </div>
    </div>
  );
}

function TechnicalDetails({ error, sectionName }: { error: Error; sectionName?: string }) {
  return (
    <details className="text-left pt-2">
      <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
        Show technical details
      </summary>
      <pre className="mt-2 bg-muted rounded-lg p-3 text-xs text-muted-foreground overflow-x-auto whitespace-pre-wrap break-words max-h-32 overflow-y-auto">
        {sectionName && `Section: ${sectionName}\n`}
        {error.message}
        {error.stack && `\n\n${error.stack}`}
      </pre>
    </details>
  );
}

function WidgetErrorFallback({ onRetry, sectionName }: { onRetry: () => void; sectionName?: string }) {
  return (
    <div className="border-2 border-dashed border-muted rounded-xl p-6 flex flex-col items-center justify-center gap-2 min-h-[120px]">
      <span className="text-muted-foreground text-2xl">⚠️</span>
      <p className="text-sm text-muted-foreground text-center">
        This section couldn't load right now.
      </p>
      <button
        onClick={onRetry}
        className="text-xs text-primary hover:underline font-medium"
      >
        Try again
      </button>
    </div>
  );
}
